import { Application, CatalogueItem, PriceType, Solution } from 'src/features/common/types';
import { Collection } from 'src/features/common/types/application/collection';
import { DeliveryOptions } from 'src/features/common/types/application/delivery-options';
import { AddressListItem } from 'src/features/order/helpers/delivery-helpers';
import { AppDelivery } from '../types/application/delivery';
interface Address {
  addressLines: string[];
  deliveryOptions?: DeliveryOptions[];
  deliveryOption?: string;
  onChangeDeliveryOption: (value: DeliveryOptions) => void;
  productId: string;
  addressId: string | undefined;
  productDetails: {
    name: string;
    image: string;
    quantity: number;
    price: number;
    priceType: PriceType;
  };
  secondaryAcquiringChannel?: string;
}

function getDeliveryOption(productId: string, products: CatalogueItem[]) {
  const product = products.find((item) => item.device.id === productId);

  if (!product) {
    return DeliveryOptions.Deliver;
  }

  if (product.device.deliveryOptions.length > 1) {
    return DeliveryOptions.Deliver;
  }

  if (
    product.device.deliveryOptions.length === 1 &&
    product.device.deliveryOptions.includes(DeliveryOptions.Stock)
  ) {
    return DeliveryOptions.Stock;
  }

  return DeliveryOptions.Deliver;
}

export function getThirdPartyDefaultSolutions(
  defaultSolutions: Solution[],
  thirdPartyDefaultAddressSolutions: Solution[],
  products: CatalogueItem[],
) {
  return defaultSolutions.map((firstSolution) => {
    const correspondingObj = thirdPartyDefaultAddressSolutions.find(
      (secondSolution) => secondSolution.productId === firstSolution.productId,
    );
    if (correspondingObj) {
      return {
        ...firstSolution,
        deliveryOption:
          correspondingObj.deliveryOption ||
          getDeliveryOption(correspondingObj.productId, products),
      };
    }
    return firstSolution;
  });
}

function getAddress(addressId: string, application: Application) {
  const address = application.addresses?.find((address) => address.id === addressId);

  return [
    address?.street || '',
    address?.city || '',
    address?.province || '',
    address?.postalCode || '',
  ];
}

export function getThirdPartySolutions(
  application: Application,
  onChangeDelivery?: (deliveryOption: DeliveryOptions, device: Collection) => void,
): Address[] {
  const collectionDevices = [...(application.collections || [])];
  const deliveryDevices = [...(application.deliveries || [])];
  const filteredDeliveryDevices = deliveryDevices.filter((device) => device.quantity > 0);
  const filteredCollectionDevices = collectionDevices.filter((device) => device.quantity > 0);

  const collections = filteredCollectionDevices.map((device) => {
    const cartItem = application.cart?.find(
      (item) => item.productId === device.productId && item.id === device.cartItemId,
    );

    return {
      deliveryId: device.id,
      addressLines: getAddress(device.addressId || '', application),
      deliveryOptions: cartItem?.deliveryOptions,
      productId: device.productId,
      addressId: device.addressId || 'default',
      onChangeDeliveryOption: (delivery) => {
        onChangeDelivery && onChangeDelivery(delivery, device);
      },
      deliveryOption: DeliveryOptions.Stock,
      productDetails: {
        quantity: device.quantity,
        name: cartItem?.title || '',
        image: cartItem?.image || '',
        price: cartItem?.price || 0,
        priceType: cartItem?.priceType || PriceType.ONCE_OFF,
      },
    };
  });

  const deliveries = filteredDeliveryDevices.map((device) => {
    const cartItem = application.cart?.find(
      (item) => item.productId === device.productId && item.id === device.cartItemId,
    );

    return {
      deliveryId: device.id,
      addressLines: getAddress(device.addressId || '', application),
      deliveryOptions: cartItem?.deliveryOptions,
      productId: device.productId,
      addressId: device.addressId || 'default',
      onChangeDeliveryOption: (delivery) => {
        onChangeDelivery && onChangeDelivery(delivery, device);
      },
      deliveryOption: DeliveryOptions.Deliver,
      productDetails: {
        quantity: device.quantity,
        name: cartItem?.title || '',
        image: cartItem?.image || '',
        price: cartItem?.price || 0,
        priceType: cartItem?.priceType || PriceType.ONCE_OFF,
      },
    };
  });

  return [...collections, ...deliveries]
    .filter((item) => item.productDetails.quantity)
    .sort((a, b) => {
      // First, compare the address lines
      const addressComparison = a.addressLines.join('').localeCompare(b.addressLines.join(''));
      if (addressComparison !== 0) return addressComparison;

      // If the address lines are the same, compare the device names
      const nameComparison = a.productDetails.name.localeCompare(b.productDetails.name);
      if (nameComparison !== 0) return nameComparison;

      // If the device names are the same, compare the price types
      return b.productDetails.priceType.localeCompare(a.productDetails.priceType);
    });
}

export function getThirdPartySolutionsV2(
  application: Application,
  onChangeDelivery?: (deliveryOption: DeliveryOptions, device: Collection) => void,
): Address[] {
  const collectionDevices = [...(application.collections || [])];
  const deliveryDevices = [...(application.deliveries || [])];
  let filteredDeliveryDevices = deliveryDevices.filter((device) => device.quantity > 0);
  let filteredCollectionDevices = collectionDevices.filter((device) => device.quantity > 0);

  // If an item in the cart needs payment and remote payments is disabled, it can only be collected
  const disableDeliveries =
    (application.cart ?? []).every((cartItem) => cartItem.priceType === PriceType.ONCE_OFF) &&
    !application.remotePaymentsEnabled;

  // If we have an item in the cart that can only be delivered
  const disableCollections = application.cart?.some(
    (cartItem) =>
      cartItem.deliveryOptions.length === 1 &&
      cartItem.deliveryOptions.includes(DeliveryOptions.Deliver) &&
      application.remotePaymentsEnabled,
  );

  if (disableDeliveries) {
    filteredCollectionDevices = [
      ...filteredCollectionDevices,
      ...(filteredDeliveryDevices as Collection[]),
    ];
    filteredDeliveryDevices = [];
  }

  // Transfer all collections to deliveries
  if (disableCollections) {
    filteredDeliveryDevices = [
      ...filteredDeliveryDevices,
      ...(filteredCollectionDevices as AppDelivery[]),
    ];
    filteredCollectionDevices = [];
  }

  const collections = filteredCollectionDevices.map((device) => {
    const cartItem = application.cart?.find((item) => item.productId === device.productId);
    return {
      deliveryId: device.id,
      addressLines: getAddress(device.addressId || '', application),
      deliveryOptions: cartItem?.deliveryOptions,
      productId: device.productId,
      addressId: device.addressId || 'default',
      onChangeDeliveryOption: (delivery) => {
        onChangeDelivery && onChangeDelivery(delivery, device);
      },
      deliveryOption: DeliveryOptions.Stock,
      productDetails: {
        quantity: device.quantity,
        name: cartItem?.title || '',
        image: cartItem?.image || '',
        price: cartItem?.price || 0,
        priceType: cartItem?.priceType || PriceType.ONCE_OFF,
      },
    };
  });

  const deliveries = filteredDeliveryDevices.map((device) => {
    const cartItem = application.cart?.find((item) => item.productId === device.productId);

    return {
      deliveryId: device.id,
      addressLines: getAddress(device.addressId || '', application),
      deliveryOptions: cartItem?.deliveryOptions,
      productId: device.productId,
      addressId: device.addressId || 'default',
      onChangeDeliveryOption: (delivery) => {
        onChangeDelivery && onChangeDelivery(delivery, device);
      },
      deliveryOption: DeliveryOptions.Deliver,
      productDetails: {
        quantity: device.quantity,
        name: cartItem?.title || '',
        image: cartItem?.image || '',
        price: cartItem?.price || 0,
        priceType: cartItem?.priceType || PriceType.ONCE_OFF,
      },
    };
  });

  return [...collections, ...deliveries]
    .filter((item) => item.productDetails.quantity)
    .sort((a, b) => {
      // First, compare the address lines
      const addressComparison = a.addressLines.join('').localeCompare(b.addressLines.join(''));
      if (addressComparison !== 0) return addressComparison;

      // If the address lines are the same, compare the device names
      const nameComparison = a.productDetails.name.localeCompare(b.productDetails.name);
      if (nameComparison !== 0) return nameComparison;

      // If the device names are the same, compare the price types
      return b.productDetails.priceType.localeCompare(a.productDetails.priceType);
    });
}

export function getDeliveries(deliveries: Collection[], application: Application): Address[] {
  const devices = deliveries || [];

  const deliveries_ = devices.map((device) => {
    const cartItem = application.cart?.find((item) => item.productId === device.productId);

    return {
      addressLines: getAddress(device.addressId || '', application),
      deliveryOptions: cartItem?.deliveryOptions,
      productId: device.productId,
      addressId: device.addressId || 'default',
      onChangeDeliveryOption: () => { },
      productDetails: {
        quantity: device.quantity,
        name: cartItem?.title || '',
        image: cartItem?.image || '',
        price: cartItem?.price || 0,
        priceType: cartItem?.priceType || PriceType.ONCE_OFF,
      },
    };
  });

  return deliveries_
    .filter((item) => item.productDetails.quantity)
    .sort((a, b) => a.addressLines.join('').localeCompare(b.addressLines.join('')));
}

export function getDeliveryAddressList(
  application: Application,
  onDelete: (addressId: string, addressLines: string[]) => void,
  onEdit: (addressId: string, addressLines: string[]) => void,
): AddressListItem[] {
  const addresses = application.addresses || [];

  return addresses.map((address) => {
    const address_ = getAddress(address.id || '', application);
    return {
      addressLines: address_,
      onDeleteButtonClicked: () => onDelete(address.id || '', address_),
      onEditButtonClicked: () => onEdit(address.id || '', address_),
    };
  });
}
